'use client';

import { usePathname } from 'next/navigation';
import { FC, PropsWithChildren, createContext, useContext } from 'react';
import { REGION_HOSTS, RegionLocale } from '~/infra/locale';

import { CITIES, City } from './constants';

interface CityContextValue {
  cities: City[];
  selectedCity: City;
  address: string | null;
  getCityHref: (id: RegionLocale) => string;
}

const CityContext = createContext<CityContextValue>({
  cities: CITIES,
  selectedCity: CITIES[0],
  address: null,
  getCityHref: () => '',
});

export const useCity = () => useContext(CityContext);

interface Props {
  locale: RegionLocale;
  address: string | null;
}

const CityProvider: FC<PropsWithChildren<Props>> = ({ locale, address, children }) => {
  const pathname = usePathname();
  const selectedCity = CITIES.find((city) => city.id === locale) ?? CITIES[0];

  const getCityHref = (id: RegionLocale): string => {
    if (id === locale) return pathname;

    const host = REGION_HOSTS[id];
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    const search = typeof window !== 'undefined' ? window.location.search : '';

    return `${protocol}//${host}${pathname}${search}`;
  };

  return (
    <CityContext.Provider value={{ cities: CITIES, selectedCity, address, getCityHref }}>
      {children}
    </CityContext.Provider>
  );
};

export default CityProvider;
